import { Config } from './Config.js'
import { Router } from './Router.js'
import { Layout } from './Layout.js'
import { Loader } from './Loader.js'
import { Nav } from './Nav.js'
import { Raf } from './Raf.js'
import { Dom } from '../utils/Dom.js'
import { Default } from '../views/Default.js'
import { Home } from '../views/Home.js'
import { Cookies } from './Cookies.js'
import { DefaultTransition } from '@/transitions/DefaultTransition.js'
import { PageOverlay } from '@/components/PageOverlay.js'
import { ThirdPartyScripts } from './ThirdPartyScripts.js'
import { WebVitals } from './WebVitals.js'
import { ScrollFade } from '../utils/ScrollFade.js'

export class App {
  static async init() {
    await Dom()
    Loader.init()
    this.initFontReadinessTracking()

    this.promise = new Promise((resolve) => {
      this.resolve = resolve
    })

    // Load critical components immediately
    this.loadGlobalComponents()

    Cookies.init()
    Raf.init()
    Nav.init()
    Layout.init()
    ScrollFade.init()
    Router.init({
      views: {
        default: Default,
        home: Home,
      },
      transitions: {
        default: DefaultTransition,
      },
    })

    Router.on('NAVIGATE_END', this.reload.bind(this))

    this.resolve()
    WebVitals.init({
      endpoint: '/api/performance/web-vitals/',
      sampleRate: 1,
    })

    // Expose ThirdPartyScripts to window for template access
    window.ThirdPartyScripts = ThirdPartyScripts

    // Defer non-critical components after page interactive
    this.loadNonCriticalComponents()
  }

  static initFontReadinessTracking() {
    if (!document.fonts || !document.fonts.ready) return

    document.fonts
      .ready
      .then(() => document.documentElement.classList.add('fonts-ready'))
      .catch(() => {})
  }

  static async preload() {
    Router.preload('/', true)
  }

  static loadGlobalComponents() {
    this.pageOverlayEl = document.querySelector('.c-page-overlay')
    if (this.pageOverlayEl) this.pageOverlay = new PageOverlay(this.pageOverlayEl, document, 'page-overlay', 'Global')
  }

  /**
   * Load non-critical components after page becomes interactive
   * This reduces initial JavaScript execution time and improves TBT
   */
  static loadNonCriticalComponents() {
    // Use requestIdleCallback to defer non-critical work
    const loadWhenIdle = (callback) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 2000 })
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(callback, 1)
      }
    }

    // Initialize third-party orchestrator immediately.
    // Script loading itself remains deferred by interaction/consent gates.
    ThirdPartyScripts.init({
      gtmId: window.__GTM_ID__,
      axeptioId: window.__AXEPTIO_ID__,
      environment: Config.ENV || 'development',
    })

    // Load dev tools only in development mode
    // if (Config.DEV) {
    //   loadWhenIdle(() => {
    //     import('../components/dev/Grid.js')
    //       .then((grid) => {
    //         this.gridInstance = new grid.Grid(this)
    //       })
    //       .catch((err) => console.warn('Failed to load Grid component:', err))
    //   })
    // }

    // if (Config.DEBUG) {
    //   loadWhenIdle(() => {
    //     import('../components/dev/Debug.js')
    //       .then((debug) => {
    //         this.debugInstance = new debug.Debug(this)
    //       })
    //       .catch((err) => console.warn('Failed to load Debug component:', err))
    //   })
    // }

    // Preload routes only after first real interaction to protect initial CWV.
    this.runOnFirstInteraction(() => {
      loadWhenIdle(() => {
        this.preload()
      })
    })
  }

  static runOnFirstInteraction(callback) {
    const events = ['pointerdown', 'mousedown', 'touchstart', 'keydown', 'scroll']
    let done = false

    const onInteract = () => {
      if (done) return
      done = true
      events.forEach((event) => {
        window.removeEventListener(event, onInteract, { passive: true })
      })
      callback()
    }

    events.forEach((event) => {
      window.addEventListener(event, onInteract, { passive: true, once: true })
    })
  }

  /**
   * Reload method called after navigation
   */
  static reload() {
    // Re-initialize components that need to be refreshed after navigation.
    ScrollFade.schedule()
  }

  static destroy() {
    Router.currentCacheEntry.view.destroy()
    // Nav.destroy()
    // Layout.destroy()
    // Router.destroy()
    // LoadingManager.reset()
  }
}
